import React, { useState, useEffect, useContext } from "react"
import { AuthContext } from "../../utils/providers/auth"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { NavLink } from "react-router-dom"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./client-receipts.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import exportToExcel from "./_utils/export"

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// Which date the month/year filters apply to
const FILTER_BY = {
	PAYMENT: "Payment date",
	DEPARTURE: "Departure date",
}
const FILTER_BY_OPTIONS = {
	[FILTER_BY.PAYMENT]: FILTER_BY.PAYMENT,
	[FILTER_BY.DEPARTURE]: FILTER_BY.DEPARTURE,
}

// Returns a dashboard for viewing all the client receipts
export default function ClientReceipts() {
	const [loading, setLoading] = useState(false)
	const [payments, setPayments] = useState([])

	const [exportArr, setExportArr] = useState([])
	const [exportTitle, setExportTitle] = useState("")

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")
	const [filterBy, setFilterBy] = useState(FILTER_BY.PAYMENT)

	// Pull the user object from the global context
	const { user } = useContext(AuthContext)

	// When the payments are updated
	useEffect(() => {
		// Clear any previous export if there are no payments
		if (payments.length === 0 || !user?.can_export) {
			setExportArr([])
			return
		}

		// Build a file name from the parameters
		const filterLabel = filterBy === FILTER_BY.DEPARTURE ? "ByDeparture" : "ByPaidDate"
		setExportTitle(`ClientReceipts_${filterLabel}_${month ? month : "All"}${year}`)

		// Otherwise, loop through the payments
		const exportData = payments.map((payment) => {
			// Pull all the data from the payment object
			const { paid_date_string, amount, payee, reference, booking, agent } = payment

			// Return the booking details
			return {
				paid_date: paid_date_string,
				amount_paid: amount,
				payee: payee,
				reference: reference,
				booking_reference: booking?.reference,
				check_in: booking?.check_in_string,
				saved_by: agent?.first_name && `${agent?.first_name} ${agent?.last_name}`,
			}
		})

		// Set the export data into the state
		setExportArr(exportData)
	}, [payments])

	// Resolves an agent's user document, cached so each agent is only fetched once per run
	const fetchAgent = (agentCache, userID) => {
		if (!userID) return Promise.resolve({})
		if (!agentCache[userID]) {
			agentCache[userID] = db
				.collection("users")
				.doc(userID)
				.get()
				.then((agentDoc) => (agentDoc.exists ? { id: agentDoc.id, ...agentDoc.data() } : {}))
		}
		return agentCache[userID]
	}

	// Shapes a booking document into the fields the table and export need
	const formatBooking = (bookingDoc) => ({
		id: bookingDoc.id,
		check_in_string: moment(bookingDoc.data().check_in?.seconds, "X").format("MMM D[,] YYYY"),
		booked_string: moment(bookingDoc.data().booked?.seconds, "X").format("MMM D[,] YYYY"),
		...bookingDoc.data(),
	})

	// Combines a payment document with its booking and agent
	const buildPaymentRow = async (paymentDoc, booking, agentCache) => {
		const paymentData = paymentDoc.data()
		return {
			paymentID: paymentDoc.id,
			...paymentData,
			amount_paid: new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(paymentData.amount),
			paid_date_string: moment(paymentData.paid_date?.seconds, "X").format("MMM D[,] YYYY"),
			booking,
			agent: await fetchAgent(agentCache, paymentData.saved_by),
		}
	}

	// Payments received within the range, whatever the booking's departure date
	const fetchByPaymentDate = async (startTimestamp, endTimestamp, agentCache) => {
		const querySnapshot = await db.collectionGroup("payments").where("paid_date", ">=", startTimestamp).where("paid_date", "<=", endTimestamp).get()

		return Promise.all(
			querySnapshot.docs.map(async (paymentDoc) => {
				const bookingRef = paymentDoc.ref.parent.parent
				const bookingDoc = bookingRef ? await bookingRef.get() : null
				const booking = bookingDoc?.exists ? formatBooking(bookingDoc) : {}
				return buildPaymentRow(paymentDoc, booking, agentCache)
			})
		)
	}

	// Every payment on bookings departing within the range, whenever it was paid
	const fetchByDepartureDate = async (startTimestamp, endTimestamp, agentCache) => {
		const bookingsSnapshot = await db.collection("bookings").where("check_in", ">=", startTimestamp).where("check_in", "<=", endTimestamp).get()

		const rowsPerBooking = await Promise.all(
			bookingsSnapshot.docs.map(async (bookingDoc) => {
				const booking = formatBooking(bookingDoc)
				const paymentsSnapshot = await bookingDoc.ref.collection("payments").get()
				return Promise.all(paymentsSnapshot.docs.map((paymentDoc) => buildPaymentRow(paymentDoc, booking, agentCache)))
			})
		)

		// Group by departure, then oldest payment first within each booking
		return rowsPerBooking
			.flat()
			.sort((a, b) => (a.booking.check_in?.seconds || 0) - (b.booking.check_in?.seconds || 0) || (a.paid_date?.seconds || 0) - (b.paid_date?.seconds || 0))
	}

	const queryPaymentsCollection = async () => {
		setLoading(true)

		// Get the milliseconds timestamps from the chosen filters
		let fetchStart = 0
		let fetchEnd = 0
		if (month && year) {
			fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
			fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		} else if (!month && year) {
			fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
			fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
		}
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)

		try {
			const agentCache = {}
			const results =
				filterBy === FILTER_BY.DEPARTURE
					? await fetchByDepartureDate(startTimestamp, endTimestamp, agentCache)
					: await fetchByPaymentDate(startTimestamp, endTimestamp, agentCache)
			setPayments(results)
		} catch (err) {
			console.error(err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Client Receipts</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Filter by:"
					placeholder="Filter by:"
					value={filterBy}
					activeOnHover={true}
					onSelect={(option) => setFilterBy(option.option)}
					options={FILTER_BY_OPTIONS}
				/>

				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={(!month || !year) && !year}
						label="Fetch stats"
						loadingText="Fetching stats..."
						loading={loading}
						onClick={() => queryPaymentsCollection()}
					/>

					{user?.can_export && (
						<div>
							<Button
								filters={true}
								disabled={exportArr.length === 0}
								label="Export to XLSX"
								onClick={() => exportToExcel(exportArr, exportTitle)}
							/>
						</div>
					)}
				</div>
			</div>

			<Table
				className="travellers-table"
				headings={["Paid date", "Amount", "Payee", "Payment Reference", "Booking Reference", "Checking in", "Saved by", ""]}
				noResults={Object.entries(payments).length === 0}
				noResultsMessage={"No results matching those filters"}>
				{payments.map((payment) => (
					<tr key={`${payment.booking?.id}-${payment.paymentID}`}>
						<td>{payment.paid_date_string}</td>
						<td>{payment.amount_paid}</td>
						<td>{payment.payee}</td>
						<td>{payment.reference}</td>
						<td>{payment.booking?.reference}</td>
						<td>{payment.booking?.check_in_string}</td>
						<td>
							{payment.agent?.first_name && `${payment.agent?.first_name} ${payment.agent?.last_name}`}
							{!payment.agent?.first_name && <small className="no-value">No name set</small>}
						</td>
						<td className="is-button">
							<NavLink to={`booking/${payment.booking?.id}`}>View booking</NavLink>
							<a
								target="_blank"
								rel="noreferrer"
								href={`booking/${payment.booking?.id}`}>
								View in new tab
							</a>
						</td>
					</tr>
				))}
			</Table>
		</Tile>
	)
}
